<template>
  <v-slide-group v-model="localSelectedIndex" class="slide-group-no-arrows mt-2">
    <v-slide-group-item v-if="isUploadEnabled">
      <div class="d-flex align-center justify-center mr-4">
        <v-btn
          variant="outlined"
          icon="mdi-plus"
          density="compact"
          @click="triggerFileInput"
        ></v-btn>
        <v-file-input
          ref="fileInput"
          v-model="selectedFile"
          accept="image/jpeg, image/png, image/avif"
          variant="outlined"
          density="comfortable"
          class="d-none"
          hide-details
          :multiple="false"
          :error-messages="fileError"
          @change="handleFileUpload"
        ></v-file-input>
      </div>
    </v-slide-group-item>
    <v-slide-group-item
      v-for="(item, index) in displayItems"
      :key="index"
      :value="index"
      v-slot="{ toggle }"
    >
      <div v-if="!hiddenIndexes.includes(index)">
        <v-card
          class="mr-4 rounded-lg"
          :class="{
            'border-md border-opacity-100 border-primary': localSelectedIndex === index,
          }"
          @click="toggle"
          :height="customHeight ? customHeight : 75"
          :width="customWidth ? customWidth : 75"
          variant="outlined"
          :disabled="editorStore.isShufflingPlacement || canvasStore.canvasLoader"
        >
          <v-container
            class="pa-0 w-100 h-100"
            v-if="typeof item !== 'string' && props.type === 'logo'"
          >
            <v-container
              class="d-flex align-center justify-center ma-0 pa-0"
              style="width: 100%; height: 100%; background-color: rgb(204, 204, 204)"
            >
              <template v-if="item.loading">
                <v-progress-circular indeterminate color="primary" />
              </template>
              <v-img
                v-else
                :src="item.logoUrl"
                max-width="100%"
                max-height="100%"
                contain
                crossorigin="anonymous"
              />
            </v-container>
          </v-container>

          <v-container v-else-if="typeof item === 'string'" class="w-100 h-100 pa-0">
            <v-img :src="item" contain crossorigin="anonymous" height="100%" max-width="100%" />
          </v-container>
        </v-card>
      </div>
      <div v-else style="display: none"></div>
    </v-slide-group-item>
  </v-slide-group>
  <p v-if="fileError" class="text-start text-error text-caption my-1">
    {{ fileError }}
  </p>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { Logo } from '@/types/logo'
import { useEditorStore } from '@/stores/editor'
import { useCanvasStore } from '@/stores/canvas'

const props = defineProps<{
  items?: string[]
  logoItems?: Logo[]
  selectedIndex: number
  type: string
  customHeight?: number
  customWidth?: number
  isUploadEnabled?: boolean
  removePadding?: boolean
  hiddenIndexes?: number[]
}>()

const emit = defineEmits(['update:selectedIndex', 'upload'])
const editorStore = useEditorStore()
const canvasStore = useCanvasStore()
const { t } = useI18n()
const hiddenIndexes = computed(() => props.hiddenIndexes ?? [])
const displayItems = computed(() => {
  return props.logoItems ? props.logoItems : props.items || []
})

const localSelectedIndex = computed({
  get: () => props.selectedIndex,
  set: (value) => emit('update:selectedIndex', value),
})

const selectedFile = ref<File | null>(null)
const fileError = ref<string | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)

const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleFileUpload = () => {
  if (selectedFile.value) {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/avif']
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'avif']

    const fileType = selectedFile.value.type
    const fileExtension = selectedFile.value.name.split('.').pop()?.toLowerCase()

    if (
      !allowedTypes.includes(fileType) ||
      !fileExtension ||
      !allowedExtensions.includes(fileExtension)
    ) {
      fileError.value = t('errors.onlyImageFilesAllowed')
      selectedFile.value = null
      return
    }

    fileError.value = null
    emit('upload', props.type, selectedFile.value)
    selectedFile.value = null
  }
}
</script>

<style scoped>
/* Hide slide group arrows but maintain sliding functionality */
:deep(.slide-group-no-arrows) {
  .v-slide-group__prev,
  .v-slide-group__next {
    display: none !important;
  }
}

:deep(.v-slide-group__container) {
  overflow-x: auto !important;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

:deep(.v-slide-group__container)::-webkit-scrollbar {
  display: none;
}

:deep(.v-slide-group__content) {
  padding-bottom: 8px !important;
}
</style>
